import { ChangeEvent, useEffect, useState } from "react";

import { Button, ModalDialog } from "@ezmid/components-library";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as yup from "yup";

import { LessonContentForm } from "./LessonContentForm";
import {
  useUpdateLessonContentByIdMutation,
  RequestNoteMetaDataUpdateRequest,
  RequestScaleMetaDataUpdateRequest,
} from "../../../api/lessons-update-content.generated";
import { useDeleteBackOfficeMediaMutation } from "../../../api/media.generated";
import { Optional } from "../../../enums/types";
import { useUpload, useUploadMedia } from "../../../hooks";
import { useGetMedia } from "../../../hooks/useGetMedia";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import type { LessonContent } from "../organisms/LessonContentCard";
import { buildTranslations, parseMetaData } from "../utils/lessonContentUtils";

type FormValues = {
  lessonContentType: string;
  order: string;
  nameEN: string;
  nameDE: string;
  textEN: string;
  textDE: string;
  status: string;
  notePlaceholder?: string;
  scaleContent?: string;
  scaleMinValue?: string;
  scaleMinLabelDE?: string;
  scaleMinLabelEN?: string;
  scaleMaxValue?: string;
  scaleMaxLabelDE?: string;
  scaleMaxLabelEN?: string;
};

type Props = {
  onModalClose: () => void;
  refetch: () => void;
  isModalOpen: boolean;
  contentDetail?: LessonContent;
};

export function UpdateLessonContentModal({
  onModalClose,
  refetch,
  isModalOpen,
  contentDetail,
}: Props) {
  const { t } = useTranslation();
  const [updateLessonContent] = useUpdateLessonContentByIdMutation();
  const [deleteMedia] = useDeleteBackOfficeMediaMutation();
  const { addFileToFormData } = useUpload();
  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);

  const FormSchema = yup.object().shape({
    lessonContentType: yup.string().required("Content type is required"),
    order: yup.string().required(t("form.required")),
    nameEN: yup.string().required(t("form.name.required")),
    textEN: yup.string().required(t("form.description")),
  });

  const enTranslations = contentDetail?.translations?.find(
    ({ language }) => language === "en"
  );
  const deTranslations = contentDetail?.translations?.find(
    ({ language }) => language === "de"
  );

  const noteMetaData = parseMetaData<RequestNoteMetaDataUpdateRequest>(
    enTranslations?.noteMetaData
  );
  const scaleMetaDataEN = parseMetaData<RequestScaleMetaDataUpdateRequest>(
    enTranslations?.scaleMetaData
  );
  const scaleMetaDataDE = parseMetaData<RequestScaleMetaDataUpdateRequest>(
    deTranslations?.scaleMetaData
  );

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
    reset,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      lessonContentType: contentDetail?.lessonContentType || "lct-theory",
      order: contentDetail?.itemOrder || "1",
      nameEN: enTranslations?.name || "",
      nameDE: deTranslations?.name || "",
      textEN: enTranslations?.text || "",
      textDE: deTranslations?.text || "",
      status: contentDetail?.status || "lcs-draft",
      notePlaceholder: noteMetaData?.placeholder || "",
      scaleContent: scaleMetaDataEN?.content || "",
      scaleMinValue: scaleMetaDataEN?.minValue?.toString() || "",
      scaleMinLabelDE: scaleMetaDataDE?.minLabel || "",
      scaleMinLabelEN: scaleMetaDataEN?.minLabel || "",
      scaleMaxValue: scaleMetaDataEN?.maxValue?.toString() || "",
      scaleMaxLabelDE: scaleMetaDataDE?.maxLabel || "",
      scaleMaxLabelEN: scaleMetaDataEN?.maxLabel || "",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const [mediaFormData, setMediaFormData] = useState<Optional<FormData>>(null);
  const [selectedFileName, setSelectedFileName] =
    useState<Optional<string>>(null);
  const [isMediaDeleted, setIsMediaDeleted] = useState<boolean>(false);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(mediaFormData);
  const { downloadedMedia, isMediaDownloading } = useGetMedia(
    !isMediaDeleted ? uploadedMediaId : undefined
  );

  const handleClose = () => {
    reset({
      lessonContentType: "lct-theory",
      order: "1",
      nameEN: "",
      nameDE: "",
      textEN: "",
      textDE: "",
      status: "lcs-draft",
      notePlaceholder: "",
      scaleContent: "",
      scaleMinValue: "",
      scaleMinLabelDE: "",
      scaleMinLabelEN: "",
      scaleMaxValue: "",
      scaleMaxLabelDE: "",
      scaleMaxLabelEN: "",
    });
    setMediaFormData(null);
    setSelectedFileName(null);
    setIsMediaDeleted(false);
    setIsDirtyFromImage(false);
    onModalClose();
  };

  const onDeleteMedia = () => {
    const mediaIdToDelete = uploadedMediaId || contentDetail?.mediaID;
    if (mediaIdToDelete) {
      deleteMedia({ id: mediaIdToDelete })
        .unwrap()
        .then(() => {
          toast.success(t("app.media.delete.success"));
        })
        .catch(() => {
          toast.warning(t("app.media.delete.failed"));
        });
    }
    setMediaFormData(null);
    setSelectedFileName(null);
    setIsMediaDeleted(true);
    setIsDirtyFromImage(true);
  };

  useEffect(() => {
    if (contentDetail && isModalOpen) {
      setIsMediaDeleted(false);
      setMediaFormData(null);
      setSelectedFileName(null);
      const enTrans = contentDetail.translations?.find(
        ({ language }) => language === "en"
      );
      const deTrans = contentDetail.translations?.find(
        ({ language }) => language === "de"
      );

      const noteMeta = parseMetaData<RequestNoteMetaDataUpdateRequest>(
        enTrans?.noteMetaData
      );
      const scaleMetaEN = parseMetaData<RequestScaleMetaDataUpdateRequest>(
        enTrans?.scaleMetaData
      );
      const scaleMetaDE = parseMetaData<RequestScaleMetaDataUpdateRequest>(
        deTrans?.scaleMetaData
      );

      reset({
        lessonContentType: contentDetail.lessonContentType || "lct-theory",
        order: contentDetail.itemOrder || "1",
        nameEN: enTrans?.name || "",
        nameDE: deTrans?.name || "",
        textEN: enTrans?.text || "",
        textDE: deTrans?.text || "",
        status: contentDetail.status || "lcs-draft",
        notePlaceholder: noteMeta?.placeholder || "",
        scaleContent: scaleMetaEN?.content || "",
        scaleMinValue: scaleMetaEN?.minValue?.toString() || "",
        scaleMinLabelDE: scaleMetaDE?.minLabel || "",
        scaleMinLabelEN: scaleMetaEN?.minLabel || "",
        scaleMaxValue: scaleMetaEN?.maxValue?.toString() || "",
        scaleMaxLabelDE: scaleMetaDE?.maxLabel || "",
        scaleMaxLabelEN: scaleMetaEN?.maxLabel || "",
      });
    }
  }, [contentDetail, isModalOpen, reset]);

  const onSubmit = () => {
    const values = getValues();
    const contentMediaId = contentDetail?.mediaID || undefined;
    setIsDirtyFromImage(false);

    const [enTranslation, deTranslation] = buildTranslations(values);

    updateLessonContent({
      id: contentDetail?.contentID || "",
      lessonContentUpdateRequest: {
        lessonContentType: values.lessonContentType as any,
        order: parseInt(values.order, 10),
        translations: [enTranslation, deTranslation],
        mediaId: isMediaDeleted ? undefined : uploadedMediaId || contentMediaId,
        status: values.status as any,
      },
    })
      .unwrap()
      .then(() => {
        refetch();
        handleClose();
        toast.success(t("course.update.lesson.content.success"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(
            errorMessage || t("course.update.lesson.content.failed")
          );
        }
      });
  };

  const onChangeMedia = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      setMediaFormData(addFileToFormData(file));
      setSelectedFileName(file.name);
      setIsDirtyFromImage(true);
      setIsMediaDeleted(false);
    }
  };

  const isFormDirty = isDirty || isDirtyFromImage;
  const isSubmitButtonDisabled = !isFormDirty || isMediaUploading;

  return (
    <ModalDialog
      id="lesson-content-update"
      title={t("course.lesson.update.content")}
      isOpen={isModalOpen}
      overlayStyles={{
        zIndex: 1000,
        overflow: "visible",
      }}
      dialogStyles={{
        zIndex: 1000,
        overflow: "auto",
        maxHeight: "90vh",
      }}
      onRequestClose={handleClose}
      footerControls={
        <>
          <Button type="button" onClick={handleClose}>
            {t("button.close")}
          </Button>
          <div className="px-2">
            <Button onClick={onSubmit} disabled={isSubmitButtonDisabled}>
              {t("button.save")}
            </Button>
          </div>
        </>
      }
    >
      <LessonContentForm
        control={control}
        errors={errors}
        onMediaFileChange={onChangeMedia}
        handleSubmit={handleSubmit(onSubmit)}
        uploadedMedia={!isMediaDeleted ? downloadedMedia : undefined}
        isMediaUploading={isMediaUploading || isMediaDownloading}
        selectedFileName={selectedFileName || undefined}
        existingMedia={!isMediaDeleted ? contentDetail?.media : undefined}
        onMediaDelete={onDeleteMedia}
      />
    </ModalDialog>
  );
}
